import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Home, Brain, FolderOpen, CircleCheckBig, Lightbulb, CalendarDays } from 'lucide-react';
import { authAPI, creatorAPI } from '../../api';
import { getTranslation } from '../../i18n';
import QuickCapture from './components/QuickCapture';
import OverviewSections from './components/OverviewSections';
import AreaTiles from './components/AreaTiles';
import AreaView from './components/AreaView';
import ItemView from './components/ItemView';
import ContinueSection from './components/ContinueSection';
import DecisionsView from './components/DecisionsView';
import TimelineView from './components/TimelineView';
import RecentActivity from './components/RecentActivity';

// Creator Space — the creator's external memory. Calm by design.
export default function CreatorSpaceModule({ view: forcedView }) {
  const navigate = useNavigate();
  const { areaKey, itemId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';

  const [lang, setLang] = useState('en');
  const [firstName, setFirstName] = useState('');
  const [overview, setOverview] = useState(null);
  const [toast, setToast] = useState(null);
  const [searchDraft, setSearchDraft] = useState(query);
  const [refreshKey, setRefreshKey] = useState(0);

  const t = getTranslation(lang);
  const locale = t.locale || 'en-GB';
  document.title = 'Maestra - Creator Space';

  const fetchOverview = useCallback(() => {
    creatorAPI.overview().then(({ data }) => setOverview(data)).catch(() => {});
  }, []);

  useEffect(() => {
    authAPI.getSettings().then(({ data }) => {
      setLang(data.language || 'en');
      setFirstName((data.username || '').trim().split(/\s+/)[0] || '');
    }).catch(() => {});
    fetchOverview();
  }, [fetchOverview]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const openItem = (item) => navigate(`/creator/item/${item.id}`);
  const openArea = (key) => navigate(`/creator/area/${key}`);
  const goHome = () => { setSearchParams({}); setSearchDraft(''); navigate('/creator'); };
  const onChanged = () => { fetchOverview(); setRefreshKey((k) => k + 1); };

  const toggleDone = async (item) => {
    await creatorAPI.update(item.id, { status: item.status === 'done' ? 'open' : 'done' });
    onChanged();
  };

  const runSearch = () => {
    const v = searchDraft.trim();
    if (!v) { goHome(); return; }
    navigate(`/creator?q=${encodeURIComponent(v)}`);
    setSearchParams({ q: v });
  };

  const view = forcedView || (itemId ? 'item' : areaKey ? 'area' : query ? 'search' : 'dashboard');

  const hour = new Date().getHours();
  const greeting = hour < 12 ? (t.wd_greet_morning || 'Good morning')
    : hour < 18 ? (t.wd_greet_afternoon || 'Good afternoon')
    : (t.wd_greet_evening || 'Good evening');

  const NAV = [
    { key: 'dashboard', label: t.cr_nav_overview || 'Overview', icon: Home, to: '/creator' },
    { key: 'brain_dump', label: 'Brain Dump', icon: Brain, to: '/creator/area/brain_dump' },
    { key: 'projects', label: t.cr_nav_projects || 'Projects', icon: FolderOpen, to: '/creator/projects' },
    { key: 'todo', label: t.cr_nav_todo || 'To Do', icon: CircleCheckBig, to: '/creator/todo' },
    { key: 'ideas', label: t.cr_nav_ideas || 'Notes & Ideas', icon: Lightbulb, to: '/creator/ideas' },
    { key: 'timeline', label: t.cr_your_timeline || 'Your timeline', icon: CalendarDays, to: '/creator/timeline' },
  ];
  const activeNav = view === 'area' && areaKey === 'brain_dump' ? 'brain_dump' : view;

  return (
    <div className="crs-layout" data-testid="creator-layout">
      <aside className="crs-side" data-testid="creator-sidebar">
        <div className="crs-side__brand">
          <span className="crs-side__wordmark">MAESTRA</span>
          <span className="crs-side__module">{t.cr_title || 'Creator Space'}</span>
        </div>
        <nav className="crs-side__nav">
          {NAV.map(({ key, label, icon: Icon, to }) => (
            <button
              key={key}
              type="button"
              className={`crs-side__item${activeNav === key ? ' is-active' : ''}`}
              data-testid={`creator-nav-${key}`}
              onClick={() => { setSearchParams({}); setSearchDraft(''); navigate(to); }}
            >
              <Icon size={15} strokeWidth={1.5} />
              <span>{label}</span>
            </button>
          ))}
        </nav>
        <button
          type="button"
          className="crs-side__back"
          onClick={() => navigate('/dashboard')}
          data-testid="creator-back-workspace"
        >← {t.cr_back || 'Workspace'}</button>
      </aside>

      <div className="crs fade-in" data-testid="creator-space">
      <header className="crs-header">
        <div>
          <p className="crs-eyebrow">{t.cr_title || 'Creator Space'}</p>
          <h1 className="crs-title" data-testid="creator-greeting">
            {view === 'dashboard' && firstName ? <>{greeting}, <em>{firstName}.</em></> : (t.cr_title || 'Creator Space')}
          </h1>
          <p className="crs-sub">{t.cr_sub || 'Capture first. Organize later.'}</p>
        </div>
        <div className="crs-search">
          <input
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
            placeholder={t.cr_search_placeholder || 'Search everything, including history\u2026'}
            data-testid="creator-search-input"
          />
        </div>
      </header>

      {view === 'item' ? (
        <ItemView itemId={itemId} t={t} locale={locale} onBack={() => navigate(-1)} onChanged={onChanged} />
      ) : view === 'decisions' ? (
        <DecisionsView t={t} locale={locale} onBack={goHome} />
      ) : view === 'timeline' ? (
        <TimelineView t={t} locale={locale} onBack={goHome} />
      ) : ['projects', 'todo', 'ideas'].includes(view) ? (
        <AreaView
          typeFilter={view === 'projects' ? 'project' : view === 'todo' ? 'task' : 'idea'}
          title={view === 'projects' ? (t.cr_nav_projects || 'Projects') : view === 'todo' ? (t.cr_nav_todo || 'To Do') : (t.cr_nav_ideas || 'Notes & Ideas')}
          t={t}
          locale={locale}
          onOpen={openItem}
          onBack={goHome}
          refreshKey={refreshKey}
        />
      ) : view === 'area' || view === 'search' ? (
        <AreaView
          areaKey={areaKey}
          query={view === 'search' ? query : ''}
          t={t}
          locale={locale}
          onOpen={openItem}
          onBack={goHome}
          refreshKey={refreshKey}
        />
      ) : (
        <>
          <QuickCapture t={t} onCaptured={() => { showToast(t.cr_captured || 'Saved to Brain Dump'); fetchOverview(); }} />
          <ContinueSection items={overview?.continue_items || []} t={t} locale={locale} />
          <OverviewSections overview={overview} t={t} locale={locale} onOpen={openItem} onToggleDone={toggleDone} />
          <AreaTiles areas={overview?.areas} t={t} locale={locale} onOpen={openArea} />
          <RecentActivity events={overview?.recent_activity || []} t={t} locale={locale} />
        </>
      )}

      {toast && <div className="toast" data-testid="creator-toast">{toast}</div>}
      </div>
    </div>
  );
}
